import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Stack,
    Typography,
    Divider,
    Tabs,
    Tab,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
    PRINCT_COLOR_ALIAS,
    PRINCT_SIDE,
    PRINT_ADDITIONAL_SERVICES,
} from "../../../utils/Constant";
import { apiRequest } from "../../../utils/config/apiRequest";
import { apiUrl } from "../../../utils/endPoints/apiUrl";
import AsyncAutocomplete from "../../comman/AsyncAutoComplete";
import PrintPriceListing from "./PrintPriceListing";

/* ---------- TAB PANEL ---------- */
function TabPanel({ value, index, children }) {
    return (
        <div hidden={value !== index}>
            {value === index && <Box pt={3}>{children}</Box>}
        </div>
    );
}

export default function AddPrintPrice({ shopId = "", onClose = () => { } }) {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            _id: "",
            printColor: [],
            paperSize: [],
            printSide: [],
            paperType: [],
            additionalServices: [],
        },
        validationSchema: Yup.object({
            printColor: Yup.array().min(1, "Select print color"),
            paperSize: Yup.array().min(1, "Select paper size"),
            printSide: Yup.array().min(1, "Select print side"),
            paperType: Yup.array().min(1, "Select paper type"),
        }),
        onSubmit: handleSave,
    });

    const { values, setValues, setFieldValue, errors, touched, handleSubmit, isValid } =
        formik;

    useEffect(() => {
        fetchPrintPriceSetting();
    }, []);

    const fetchPrintPriceSetting = async () => {
        const { data, status } = await apiRequest({
            endUrl: apiUrl.branch.addEditPrintSetting(shopId),
        });

        if (!status) return;

        setValues({
            _id: data?._id,
            paperSize: data?.paperSize?.map(p => ({ _id: p._id, name: p.paperName })) || [],
            printColor: data?.printingColor?.map(c => ({ name: c.colorName })) || [],
            printSide: data?.printSide?.map(s => ({ name: s.sideName })) || [],
            paperType: data?.paperType?.map(p => ({ _id: p._id, name: p.paperTypeName })) || [],
            additionalServices:
                data?.additionCharge?.map(a => ({ name: a.chargeName })) || [],
        });
    };

    async function handleSave() {
        setLoading(true);

        const payload = {
            paperSize: values.paperSize.map(p => ({
                paperSizeId: p._id,
                paperName: p.name,
            })),
            printingColor: values.printColor.map(c => ({ colorName: c.name })),
            printSide: values.printSide.map(s => ({ sideName: s.name })),
            paperType: values.paperType.map(p => ({
                paperTypeId: p._id,
                paperTypeName: p.name,
            })),
            additionCharge: values.additionalServices.map(a => ({
                chargeName: a.name,
            })),
        };

        const { status } = await apiRequest({
            method: values._id ? "PUT" : "POST",
            endUrl: apiUrl.branch.addEditPrintSetting(shopId),
            body: payload,
            showMsg: true,
        });

        setLoading(false);

        if (status) {
            setTab(1);
        }
    }

    const Label = ({ text, required }) => (<label>
        {text} {required && <span className="text-danger">*</span>}
    </label>);

    const fetchOptions = async (url, text) => {
        const { data } = await apiRequest({
            endUrl: url,
            query: { searchQ: text }
        });

        return data?.rows || []
    }

    return (
        <Box>

            {/* ----------- TABS HEADER ----------- */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
            >
                <Tab label="Print Settings" />
                <Tab label="Price Listing" />
            </Tabs>

            {/* ----------- SETTINGS TAB ----------- */}
            <TabPanel value={tab} index={0}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Required Settings</Typography>
                        <Divider />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Label text="Printing Color" required />
                        <AsyncAutocomplete
                            prefetch
                            multiple
                            value={values.printColor}
                            onChange={v => setFieldValue("printColor", v)}
                            fetchOptions={() => PRINCT_COLOR_ALIAS}
                            getOptionLabel={o => o.name}
                            isOptionEqualToValue={(value, option) => value.name === option.name}
                            error={touched.printColor && !!errors.printColor}
                            helperText={touched.printColor && errors.printColor}
                        />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Label text="Paper Size" required />
                        <AsyncAutocomplete
                            prefetch
                            multiple
                            value={values.paperSize}
                            onChange={v => setFieldValue("paperSize", v)}
                            fetchOptions={(text) => fetchOptions(apiUrl.masters.paperSize, text)}
                            getOptionLabel={o => o.name}
                            isOptionEqualToValue={(value, option) => value.name === option.name}
                            error={touched.paperSize && !!errors.paperSize}
                            helperText={touched.paperSize && errors.paperSize}
                        />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Label text="Paper Type" required />
                        <AsyncAutocomplete
                            prefetch
                            multiple
                            value={values.paperType}
                            onChange={v => setFieldValue("paperType", v)}
                            fetchOptions={(text) => fetchOptions(apiUrl.masters.paperType, text)}
                            getOptionLabel={o => o.name}
                            isOptionEqualToValue={(value, option) => value.name === option.name}
                            error={touched.paperType && !!errors.paperType}
                            helperText={touched.paperType && errors.paperType}
                        />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Label text="Print Side" required />
                        <AsyncAutocomplete
                            prefetch
                            multiple
                            value={values.printSide}
                            onChange={v => setFieldValue("printSide", v)}
                            fetchOptions={() => PRINCT_SIDE}
                            getOptionLabel={o => o.name}
                            isOptionEqualToValue={(value, option) => value.name === option.name}
                            error={touched.printSide && !!errors.printSide}
                            helperText={touched.printSide && errors.printSide}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6">Optional</Typography>
                        <Divider />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Label text="Additional Services" />
                        <AsyncAutocomplete
                            prefetch
                            multiple
                            value={values.additionalServices}
                            onChange={v => setFieldValue("additionalServices", v)}
                            isOptionEqualToValue={(value, option) => value.name === option.name}
                            fetchOptions={() => PRINT_ADDITIONAL_SERVICES}
                            getOptionLabel={o => o.name}
                        />
                    </Grid>

                    <Grid item xs={12} textAlign="center">
                        <LoadingButton
                            variant="contained"
                            size="large"
                            loading={loading}
                            disabled={!isValid}
                            onClick={handleSubmit}
                        >
                            Save & Continue
                        </LoadingButton>
                    </Grid>
                </Grid>
            </TabPanel>

            {/* ----------- LISTING TAB ----------- */}
            <TabPanel value={tab} index={1}>
                <PrintPriceListing shopId={shopId} />
            </TabPanel>

        </Box>
    );
}
